import {
  ButtonComponent,
  DropDownComponent,
  CurrenciesSelector,
  Loader,
} from "@/components";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { CURRENCIES } from "@/data/data.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, ApiResponse } from "@/types/Api.tsx";
import { FormError, ValidationErrors } from "@/types/ValidationError.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import {
  CardPayment,
  CreateExchange,
  ExchangeRate,
  ExchangeSettings,
  IBANAcc,
} from "@/types/Exchange.tsx";
import { AxiosError, isAxiosError } from "axios";
import { formatError } from "@/utils/formatError.tsx";
import { Currency } from "@/types/Currency.tsx";
import { CreateTransaction, Transaction } from "@/types/Transaction.tsx";
import {
  BalanceQueryKey,
  BankAccountQueryKey,
  CryptoAccountQueryKey,
  CurrencyQueryKey,
  DepositWalletQueryKey,
  ExchangeSettingsQueryKey,
  GetTransactionsQueryKey,
  IBANAccountQueryKey,
  // ListDepositWalletQueryKey,
  PaymentCheckoutQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import customArrow from "@/assets/SVG/CustomArrow.svg";
import { BankAccount, CryptoAccount } from "@/types/BankSettings";
import { getUser } from "@/utils/localStorage.tsx";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
// import { useAppDispatch, useAppSelector } from "@/store";
// import { setRefresh } from "../store/slices/refreshSlice";
import { Balance } from "@/types/Balance.tsx";
import { QRCodeCanvas } from "qrcode.react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { WalletAddress } from "@/pages/ScanWallet.tsx";
import { isBitramp } from "@/utils/utils";
import customArrowB from "@/assets/SVG/customArrowB.svg";

const networks = [
  { label: "ERC20", value: "ERC20" },
  { label: "TRC20", value: "TRC20" },
];

const Exchange: React.FC = () => {
  const { showSnackBar } = useSnackBarAlert();
  const [isRotated, setIsRotated] = useState(false);
  const [err, setError] = useState<{ type: string; message: string } | null>(
    null
  );
  const [hasError, setHasError] = useState(false);

  const [notEnoughBalanceModal, setNotEnoughBalanceModal] = useState(false);

  const [canExchange, setCanExchange] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const [exchangeData, setExchangeData] = useState<ExchangeRate | null>(null);

  // const dispatch = useAppDispatch();
  // const refresh = useAppSelector((state) => state.refresh.refresh);

  const [exchangeModal, setExchangeModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(59);
  const [exchangeStep, setExchangeStep] = useState(1);
  const ibanRef = useRef<HTMLDivElement>(null);
  const swiftRef = useRef<HTMLDivElement>(null);
  const nameAddressRef = useRef<HTMLDivElement>(null);
  const transactionIdRef = useRef<HTMLDivElement>(null);
  const [isExchangeModalClosed, setIsExchangeModalClosed] = useState(false);
  const queryClient = useQueryClient();
  const [resetSignal, setResetSignal] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "bank">("card");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("-");
  const [selectedWallet, setSelectedWallet] = useState<CryptoAccount | null>(
    null
  );

  const [manualClose, setManualClose] = useState(false);

  const [forgotWallet, setForgotWallet] = useState(false);
  const [forgotNetwork, setForgotNetwork] = useState(false);
  const [forgotBankAccount, setForgotBankAccount] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<{
    up: Currency;
    down: Currency;
  }>({
    up: CURRENCIES[2],
    down: CURRENCIES[3],
  });

  const {
    data: currencies,
    isLoading: isLoadingCurrencies,
    error: currencyError,
  } = useQuery<ApiResponse<Currency[]>>({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  // const { data: depositWallets } = useQuery<ApiResponse<WalletAddress[]>>({
  //   queryFn: async () =>
  //     GenericApi.get<WalletAddress[]>(ApiRoutes.LIST_DEPOSIT_WALLET),
  //   queryKey: [ListDepositWalletQueryKey],
  // });

  const { data: ibanAcc, isLoading: isLoadingIbanAcc } = useQuery<
    ApiResponse<IBANAcc>
  >({
    queryFn: async () => GenericApi.get<IBANAcc>(ApiRoutes.IBANACCOUNT),
    queryKey: [IBANAccountQueryKey],
  });

  const { data: cryptoAccounts, isLoading: cryptoAccountsLoading } = useQuery<
    ApiResponse<CryptoAccount[]>
  >({
    queryFn: async () =>
      GenericApi.get<CryptoAccount[]>(ApiRoutes.CRYPTOACCOUNT),
    queryKey: [CryptoAccountQueryKey],
  });

  const approvedCryptoAccounts =
    cryptoAccounts?.data?.filter(
      (account) => account.status.status === "approved"
    ) || [];

  const [qrModal, setQrModal] = useState(false);

  const { data: exchangeSettings } = useQuery<ApiResponse<ExchangeSettings>>({
    queryFn: async () =>
      GenericApi.get<ExchangeSettings>(ApiRoutes.EXCHANGESETTINGS),
    queryKey: [ExchangeSettingsQueryKey],
  });

  const sendsCryptoToCryptoWithoutBalance =
    selectedOptions.up?.type === "crypto" &&
    selectedOptions.down?.type === "crypto" &&
    !exchangeSettings?.data?.balance_enabled;
  const sendsFiatToCryptoWithoutBalance =
    selectedOptions.up?.type === "fiat" &&
    selectedOptions.down?.type === "crypto" &&
    !exchangeSettings?.data?.balance_enabled;
  const sendsCryptoToFiatWithoutBalance =
    selectedOptions.up?.type === "crypto" &&
    selectedOptions.down?.type === "fiat" &&
    !exchangeSettings?.data?.balance_enabled;

  const { selectedClient } = useSelector((state: RootState) => state.client);

  const {
    data: balance,
    isLoading: isLoadingBalance,
    error: balanceError,
  } = useQuery<ApiResponse<Balance[]>>({
    queryFn: async () => GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE),
    queryKey: [BalanceQueryKey],
  });

  const options = currencies?.data?.map((curr) => {
    return {
      code: curr.symbol ?? "",
      symbol: curr.symbol ?? "",
      name: "",
      price: 0,
      type: curr.type,
    };
  });

  const [selectedAccounts, setSelectedAccounts] = useState<{
    buy_bank_account: number | null;
    buy_crypto_account: number | null;
    sell_bank_account: number | null;
    sell_crypto_account: number | null;
  }>({
    buy_bank_account: null,
    buy_crypto_account: null,
    sell_bank_account: null,
    sell_crypto_account: null,
  });

  const handleOnChangeAccounts = (selectedId: number, index: number) => {
    setSelectedAccounts((prev) => ({
      ...prev,
      ...(index === 0
        ? selectedOptions.down.type === "fiat"
          ? { buy_bank_account: selectedId, buy_crypto_account: null }
          : { buy_crypto_account: selectedId, buy_bank_account: null }
        : selectedOptions.up.type === "fiat"
        ? { sell_bank_account: selectedId, sell_crypto_account: null }
        : { sell_crypto_account: selectedId, sell_bank_account: null }),
    }));
  };

  const setBuyAccount = (id: number, type: "fiat" | "crypto") => {
    setSelectedAccounts((prev) => ({
      ...prev,
      buy_bank_account: type === "fiat" ? id : null,
      buy_crypto_account: type === "crypto" ? id : null,
    }));
  };
  
  const setSellAccount = (id: number, type: "fiat" | "crypto") => {
    setSelectedAccounts((prev) => ({
      ...prev,
      sell_bank_account: type === "fiat" ? id : null,
      sell_crypto_account: type === "crypto" ? id : null,
    }));
  };
  

  const createExchangeMutation = useMutation<
    ApiResponse<ExchangeRate>,
    ApiError<ValidationErrors>,
    CreateExchange,
    { inputFocusedDown: boolean }
  >({
    mutationFn: (data) =>
      GenericApi.post<ExchangeRate>(ApiRoutes.EXCHANGE, {
        ...data,
        amount: data.inputFocusedDown
          ? selectedOptions.down.price
          : selectedOptions.up.price,
      }),
    onMutate: async (variables) => {
      return { inputFocusedDown: variables.inputFocusedDown };
    },
    onSuccess: async ({ data }, _vars, context) => {
      setExchangeData(data);

      if (!data?.rate) {
        showSnackBar(
          "No exchange rate found",
          "error",
          {
            vertical: "top",
            horizontal: "right",
          },
          5000
        );
        return;
      }

      if (context?.inputFocusedDown) {
        setSelectedOptions({
          ...selectedOptions,
          up: {
            ...selectedOptions.up,
            price: Number((data.rate * selectedOptions.down.price).toFixed(5)),
          },
        });
      } else {
        setSelectedOptions({
          ...selectedOptions,
          down: {
            ...selectedOptions.down,
            price: Number((data.rate * selectedOptions.up.price).toFixed(5)),
          },
        });
      }
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      setHasError(true);
    },
  });

  useEffect(() => {
    if (currencies?.data) {
      setSelectedOptions({
        up: {
          ...currencies?.data?.[0],
          name: "a",
          price: 0,
          code: currencies?.data?.[0]?.symbol ?? "",
        },
        down: {
          ...currencies?.data?.[1],
          name: "a",
          price: 0,
          code: currencies?.data?.[1]?.symbol ?? "",
        },
      });
    }
  }, [currencies?.data]);

  const checkTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTION_CHECK, {
        ...data,
        client_id: selectedClient?.id,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setIsExchangeModalClosed(true);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const [justCreatedTransaction, setJustCreatedTransaction] = useState<
    string | null
  >(null);

  const createTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTIONS, { ...data }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setJustCreatedTransaction(data?.id ?? null);
      setIsExchangeModalClosed(true);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  useEffect(() => {
    if (!exchangeModal && isExchangeModalClosed && !manualClose) {
      showSnackBar(
        "Transaction created successfully",
        "success",
        { vertical: "top", horizontal: "right" },
        5000
      );
      setIsExchangeModalClosed(false);
    }
  }, [exchangeModal, isExchangeModalClosed]);

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    const errorMessage = "Something went wrong";

    if (
      isAxiosError<ValidationErrors>(error) &&
      Array.isArray(error.response?.data?.detail)
    ) {
      setError({
        type: "manual",
        message: formatError(error, errorMessage),
      });
      return;
    }

    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message: error.response?.data?.detail ?? errorMessage,
      });
      return;
    }

    //* Set error message from server
    setError({
      type: "manual",
      message: error?.message ?? errorMessage,
    });
  };

  const handleClick = async () => {
    setIsRotated(!isRotated);

    if (hasError) {
      setHasError(false);
      selectedOptions.up.price = 0;
      selectedOptions.down.price = 0;
      setSelectedOptions({
        ...selectedOptions,
        down: selectedOptions.up,
        up: selectedOptions.down,
      });
    } else {
      setSelectedOptions({
        ...selectedOptions,
        down: selectedOptions.up,
        up: selectedOptions.down,
      });
    }

    const updatedState = {
      ...selectedOptions,
      up: selectedOptions.down,
      down: selectedOptions.up,
    };
    setSelectedOptions(updatedState);
  };

  const onChangeInput = (
    value: number,
    index: number,
    {
      inputFocusedUp,
    }: {
      inputFocusedUp: boolean;
      inputFocusedDown: boolean;
    }
  ) => {
    if (index === 0) {
      setSelectedOptions({
        ...selectedOptions,
        up: { ...selectedOptions.up, price: value },
      });
    } else {
      setSelectedOptions({
        ...selectedOptions,
        down: { ...selectedOptions.down, price: value },
      });
    }

    if (value != 0) {
      setCanExchange(true);
    } else {
      setCanExchange(false);
    }

    setHasError(false);
    setTimeout(() => {
      if (inputFocusedUp) {
        handleCreateExchange({
          from_currency_symbol: selectedOptions.up.code,
          to_currency_symbol: selectedOptions.down.code,
          inputFocusedUp: true,
          inputFocusedDown: false,
        });
      } else {
        handleCreateExchange({
          from_currency_symbol: selectedOptions.down.code,
          to_currency_symbol: selectedOptions.up.code,
          inputFocusedUp: false,
          inputFocusedDown: true,
        });
      }
    }, 700);
  };

  const handleOnChangeCurrencies = (option: Currency, index: number) => {
    setHasError(false);

    const newSelectedOptions = {
      up:
        index === 0
          ? { ...option, price: 0 }
          : { ...selectedOptions.up, price: 0 },
      down:
        index === 1
          ? { ...option, price: 0 }
          : { ...selectedOptions.down, price: 0 },
    };

    setSelectedOptions(newSelectedOptions);

    const shouldCalculate =
      index === 0
        ? selectedOptions.down.price > 0
        : selectedOptions.up.price > 0;

    if (shouldCalculate) {
      handleCreateExchange({
        from_currency_symbol:
          index === 0 ? option.code : selectedOptions.up.code,
        to_currency_symbol:
          index === 1 ? option.code : selectedOptions.down.code,
        inputFocusedUp: index === 1,
        inputFocusedDown: index === 0,
      });
    }
  };

  const { data: bankAccounts, isLoading: bankAccountsLoading } = useQuery<
    ApiResponse<BankAccount[]>
  >({
    queryKey: [BankAccountQueryKey],
    queryFn: async () => GenericApi.get<BankAccount[]>(ApiRoutes.BANKACCOUNT),
    enabled: !!currencies?.data,
  });

  const handleCreateExchange = (data: CreateExchange) => {
    if (!data.from_currency_symbol || !data.to_currency_symbol) {
      return;
    }

    setError(null);
    createExchangeMutation.mutate(data);
  };

  // useEffect(() => {
  //     if (currencies?.data) {
  //         setSelectedOptions({
  //             up: {
  //                 ...currencies.data[0],
  //                 name: "a",
  //                 price: 0,
  //                 code: currencies.data[0]?.symbol ?? "",
  //             },
  //             down: {
  //                 ...currencies.data[1],
  //                 name: "a",
  //                 price: 0,
  //                 code: currencies.data[1]?.symbol ?? "",
  //             },
  //         });
  //         // Reset any other related states
  //         setCanExchange(false);
  //         setHasError(false);
  //     }
  // }, [currencies?.data]);

  const handleCreateTransaction = async (data: CreateTransaction) => {
    checkTransactionMutation.mutate(data, {
      onSuccess: async () => {
        createTransactionMutation.mutate(data, {
          onSuccess: async () => {
            queryClient.invalidateQueries({
              queryKey: [GetTransactionsQueryKey],
            });
          },
        });
      },
      onError: async ({ error }) => {
        CustomLogger.error(error);
        handleAxiosError(error);
      },
    });
  };

  const handleCheckTransaction = async (data: CreateTransaction) => {
    checkTransactionMutation.mutate(data, {
      onSuccess: async () => {
        setExchangeModal(true);
        setExchangeStep(1);
      },
      onError: async () => {
        setNotEnoughBalanceModal(true);
      },
    });
  };

  const formattedBankOptions =
    bankAccounts?.data?.map((acc) => {
      return acc.status.status === "approved"
        ? {
            label: acc.bank_name,
            value: acc.id,
          }
        : null;
    }) || [];

  const dropdownTextUp = "Chose bank account";
  const user = getUser();
  const fullName = `${user?.business_name ?? "N/A"}`;
  const [copiedIban, setCopiedIban] = useState(false);
  const [copiedSwift, setCopiedSwift] = useState(false);
  const [copiedNameAddress, setCopiedNameAddress] = useState(false);
  const [copiedTransactionID, setCopiedTransactionID] = useState(false);

  const copyIbanToClipboard = () => {
    if (ibanRef.current) {
      navigator.clipboard.writeText(ibanRef.current.innerText).then(() => {
        setCopiedIban(true);
        setTimeout(() => setCopiedIban(false), 2000);
      });
    }
  };

  const copySwiftToClipboard = () => {
    if (swiftRef.current) {
      navigator.clipboard.writeText(swiftRef.current.innerText).then(() => {
        setCopiedSwift(true);
        setTimeout(() => setCopiedSwift(false), 2000);
      });
    }
  };

  const copyNameAddressToClipboard = () => {
    if (nameAddressRef.current) {
      navigator.clipboard
        .writeText(nameAddressRef.current.innerText)
        .then(() => {
          setCopiedNameAddress(true);
          setTimeout(() => setCopiedNameAddress(false), 2000);
        });
    }
  };

  const copyTransactionIDToClipboard = () => {
    if (transactionIdRef.current) {
      navigator.clipboard
        .writeText(transactionIdRef.current.innerText)
        .then(() => {
          setCopiedTransactionID(true);
          setTimeout(() => setCopiedTransactionID(false), 2000);
        });
    }
  };

  useEffect(() => {
    if (!exchangeModal && exchangeStep != 1) return;

    setTimeLeft(59);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (exchangeStep === 1) {
            setExchangeModal(false);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [exchangeModal, exchangeStep]);

  const postCardPayment = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CardPayment
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.CARD_PAYMENT, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setIsExchangeModalClosed(true);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const handlePostCardPayment = (data: CardPayment) => {
    data.payment_channel = "PCL_CARD";
    if (justCreatedTransaction) {
      data.transaction_id = justCreatedTransaction;
    } else {
      throw new Error("Transaction ID is null");
    }

    postCardPayment.mutate(data);
    setExchangeStep(3);
  };

  const {
    data: zenLink,
    isLoading: isLoadingZenLink,
    error: zenLinkError,
  } = useQuery<ApiResponse<string>>({
    queryFn: async () =>
      GenericApi.get<string>(
        `${ApiRoutes.PAYMENT_CHECKOUT}/${justCreatedTransaction}`
      ),
    queryKey: [PaymentCheckoutQueryKey, justCreatedTransaction],
    enabled: !!justCreatedTransaction,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setExchangeModal(false);
      }
    };

    if (exchangeModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [exchangeModal, setExchangeModal]);

  const OpenZenCard = (link: string) => {
    if (link) {
      const win = window.open(link, "_blank");
      if (win) {
        win.focus();
      } else {
        console.error("Failed to open ZenLink in a new window");
      }
    }
  };

  const route =
    selectedOptions.up?.code === "BTC"
      ? `${ApiRoutes.DEPOSIT_WALLET}?currency=${selectedOptions.up?.code}`
      : `${ApiRoutes.DEPOSIT_WALLET}?currency=${selectedOptions.up?.code}&network=${selectedNetwork}`;

  const {
    data: wallet,
    isLoading,
    error,
  } = useQuery<ApiResponse<WalletAddress>>({
    queryFn: async () => {
      const response = await GenericApi.get<WalletAddress>(route);

      if (response.error) {
        handleAxiosError(response.error);
        throw response.error;
      }
      return response;
    },
    queryKey: [
      DepositWalletQueryKey,
      selectedOptions.up?.code,
      selectedNetwork,
    ],
    enabled:
      !!selectedOptions.up?.code &&
      selectedOptions.up?.code !== "-" &&
      exchangeModal &&
      !exchangeSettings?.data?.balance_enabled,
  });

  const walletAddress = wallet?.data?.address || "";
  const walletNetwork = wallet?.data?.network || "";
  const walletCurrency = wallet?.data?.currency || "";
  const currentCurrency = !isRotated
    ? selectedOptions.down.code
    : selectedOptions.up.code;

  // const filteredNetworks =
  //   currentCurrency === "ETH"
  //     ? networks.filter((n) => n.value === "ERC20")
  //     : networks;

  const filteredNetworks =
    currentCurrency === "BTC"
      ? []
      : currentCurrency === "ETH"
      ? networks.filter((n) => n.value === "ERC20")
      : networks;

  if (
    isLoadingCurrencies ||
    bankAccountsLoading ||
    isLoadingBalance ||
    isLoadingIbanAcc ||
    cryptoAccountsLoading
  ) {
    return <Loader />;
  }

  if (
    currencyError ||
    !currencies ||
    currencies?.error ||
    !currencies?.data ||
    balanceError ||
    !balance ||
    balance?.error
  ) {
    return (
      <div>Error: {currencies?.error?.message ?? "Something went wrong"}</div>
    );
  }

  return (
    <div className="exchange">
      <div className="exchange__right">
        <div className="exchange__right__top px-8 mt-8 mb-3">
          <div className="Heading2 dark-blue bold">Convert</div>
          <div className="Title dark-blue bold flex gap-2">
            <svg
              width="30"
              height="30"
              viewBox="0 0 35 35"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Group 1000002849">
                <path
                  id="Vector"
                  d="M17.5 33.5C21.7857 33.5 25.6814 31.8129 28.5521 29.0683C31.599 26.1578 33.5 22.0498 33.5 17.5C33.5 8.66334 26.3367 1.5 17.5 1.5C8.66334 1.5 1.5 8.66334 1.5 17.5C1.5 22.0842 3.42971 26.2209 6.51865 29.1361C9.38132 31.8394 13.2157 33.5 17.5 33.5Z"
                  fill="white"
                  stroke="#222B45"
                  strokeWidth="3"
                />
                <g id="Group">
                  <path
                    id="Vector_2"
                    d="M27.5158 27.9839C24.9131 30.4724 21.3846 32.0002 17.4998 32.0002C13.615 32.0002 10.1449 30.4977 7.54834 28.0458C7.59363 27.9221 7.63631 27.8036 7.6729 27.6912C8.81394 24.2333 12.4252 20.7971 17.4632 20.7779C22.4534 20.7605 26.1448 24.1348 27.3259 27.6921C27.3895 27.7618 27.4539 27.8611 27.5158 27.9839Z"
                    fill="#222B45"
                  />
                  <path
                    id="Vector_3"
                    d="M17.4892 19.5802C16.0894 19.5628 14.8622 19.0916 13.8021 18.1927C12.5931 17.1675 11.8771 15.8513 11.5723 14.3044C11.4712 13.7913 11.4199 13.2722 11.439 12.7496C11.5043 10.9927 12.0871 9.43882 13.2943 8.14534C14.1087 7.27345 15.1008 6.67505 16.2715 6.42681C17.9456 6.0723 19.4934 6.39545 20.874 7.41716C22.2746 8.45369 23.0855 9.86301 23.4261 11.558C23.5402 12.1268 23.589 12.7026 23.5533 13.2809C23.4418 15.0883 22.7955 16.6666 21.4828 17.9374C20.6005 18.791 19.5492 19.3311 18.3245 19.5044C18.0475 19.5436 17.7662 19.5558 17.4874 19.5802H17.4892Z"
                    fill="#222B45"
                  />
                </g>
              </g>
            </svg>
            <div>{fullName}</div>
          </div>
        </div>
        <div className="exchange__right__container mb-4">
          <div className="exchange__right__container--white Container pshadow">
            <div className="flex-c w-full center-align">
              <div className="exchange__right__container--spacing">
                {/*Before refactor*/}

                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={1}
                  currency={
                    !isRotated
                      ? selectedOptions.down.code
                      : selectedOptions.up.code
                  }
                  isUp={isRotated}
                  hasError={hasError}
                  value={
                    !isRotated
                      ? Number(selectedOptions.down.price.toFixed(4))
                      : selectedOptions.up.price
                  }
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[1] ?? CURRENCIES[3]}
                  currencie={""}
                  name={` line--pink selector ${isRotated ? "up1" : "down1"} ${
                    isBitramp() ? "!bg-[#262626]" : ""
                  }`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />
                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={0}
                  isUp={!isRotated}
                  hasError={hasError}
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[0] ?? CURRENCIES[2]}
                  currency={
                    !isRotated
                      ? selectedOptions.up.code
                      : selectedOptions.down.code
                  }
                  value={
                    !isRotated
                      ? selectedOptions.up.price
                      : Number(selectedOptions.down.price.toFixed(4))
                  }
                  currencie={""}
                  name={`line--bej selector ${isRotated ? "down2" : "up2"} ${
                    isBitramp() ? "!bg-[#353535]" : ""
                  }`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />
                <div className="absoluteCenter" onClick={handleClick}>
                  <img
                    className={
                      isRotated
                        ? "rotate w-[60px] h-[60px]"
                        : "reverseRotate w-[60px] h-[60px]"
                    }
                    src={isBitramp() ? customArrowB : customArrow}
                    alt="customArrow"
                  />
                </div>
              </div>

              <div
                className={`exchange__right__container__stats Text  ${
                  isBitramp() ? "!text-[#D2D2D2]" : "dark-gray"
                }`}
              >
                <div
                  className={`${
                    exchangeData
                      ? isBitramp()
                        ? "text-[#fff]"
                        : "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Amount</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${selectedOptions.up.price} ${selectedOptions.up.code}`}
                  </div>
                </div>
                <div
                  className={`${
                    exchangeData
                      ? isBitramp()
                        ? "text-[#fff]"
                        : "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Price</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `1 ${selectedOptions.up.code} = ${Number(
                          exchangeData?.rate ?? 0
                        ).toFixed(10)} ${selectedOptions.down.code}`}
                  </div>
                </div>
                <div
                  className={`${
                    exchangeData
                      ? isBitramp()
                        ? "text-[#fff]"
                        : "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Exchange</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(selectedOptions.down.price ?? 0).toFixed(
                          10
                        )} ${selectedOptions.down.code}`}
                  </div>
                </div>
                <div
                  className={`${
                    exchangeData
                      ? isBitramp()
                        ? "text-[#fff]"
                        : "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Fee</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(
                          exchangeData?.fee.amount_percentage ?? 0
                        ).toFixed(2)}%`}
                  </div>
                </div>
                <div
                  className={`${
                    exchangeData
                      ? isBitramp()
                        ? "text-[#fff]"
                        : "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div className="font-bold">Estimated total proceeds</div>
                  <div className="font-bold">
                    {exchangeData === null ? "- -" : exchangeData?.total}
                  </div>
                </div>
              </div>
              <div className="exchange__right__container__button ">
                <div
                  className={`${!err ? "hidden" : "hidden"} error text-red-500`}
                >
                  {err?.message ? err.message : "Something went wrong"}
                </div>
                <div
                  className={`${
                    !forgotBankAccount ? "hidden" : ""
                  } error text-red-500`}
                >
                  Please don't forget to select a bank account
                </div>
                <div
                  className={`${
                    !forgotWallet ? "hidden" : ""
                  } error text-red-500`}
                >
                  Please don't forget to select a wallet
                </div>
                <div
                  className={`${
                    !forgotNetwork ? "hidden" : ""
                  } error text-red-500`}
                >
                  Please don't forget to select a wallet and network
                </div>
                <ButtonComponent
                  title={
                    createTransactionMutation.isPending
                      ? "Loading..."
                      : "Accept & Pay"
                  }
                  actionCallback={() => {
                    if (!exchangeData) return;
                    if (
                      sendsCryptoToCryptoWithoutBalance &&
                      (selectedNetwork == "-" || selectedWallet === null)
                    ) {
                      setError({
                        type: "manual",
                        message: "Please select a bank account",
                      });
                      setForgotNetwork(true);
                      setForgotWallet(false);
                      setForgotBankAccount(false);
                      return;
                    }
                    if (
                      sendsFiatToCryptoWithoutBalance &&
                      selectedWallet === null
                    ) {
                      setError({
                        type: "manual",
                        message: "Please select a crypto account",
                      });
                      setForgotWallet(true);
                      setForgotNetwork(false);
                      setForgotBankAccount(false);
                      return;
                    }
                    if (
                      sendsCryptoToFiatWithoutBalance &&
                      selectedAccounts.buy_bank_account === null
                    ) {
                      setError({
                        type: "manual",
                        message: "Please select a bank account",
                      });
                      setForgotBankAccount(true);
                      setForgotNetwork(false);
                      setForgotWallet(false);
                      return;
                    }
                    handleCheckTransaction({
                      rate_id: exchangeData?.id,
                      amount: selectedOptions.up.price,
                      buy_bank_account_id: selectedAccounts.buy_bank_account,
                      buy_crypto_account_id:
                        selectedAccounts.buy_crypto_account,
                      sell_bank_account_id:
                        selectedAccounts.sell_bank_account,
                      sell_crypto_account_id:
                        selectedAccounts.sell_crypto_account,
                      client_id: selectedClient?.id ?? null,
                    });
                  }}
                  name={`Primary_button`}
                  canClick={canExchange}
                />
              </div>
            </div>
          </div>
          <div className="exchange__right__container--gray Container pshadow relative">

  {/* BUY SIDE — FIAT */}
  {selectedOptions?.down?.type === "fiat" && (
    <DropDownComponent
      buttonText={dropdownTextUp}
      actionCallback={(selectedId) =>
        setBuyAccount(Number(selectedId), "fiat")
      }
      subtitle="Select your bank account for the transfer. To add a new one, please navigate to the Settings section."
      title="Choose"
      placeHolder={dropdownTextUp}
      options={
        formattedBankOptions.filter(
          (option): option is { label: string; value: number } =>
            option !== null
        )
      }
      resetSignal={resetSignal}
    />
  )}

  {/* BUY SIDE — CRYPTO */}
  {(sendsCryptoToCryptoWithoutBalance ||
    sendsFiatToCryptoWithoutBalance) && (
    <DropDownComponent
      buttonText="Select crypto account"
      actionCallback={(walletId: string | number) => {
        const wallet =
          approvedCryptoAccounts?.find(
            (wallet) => wallet.id == walletId
          ) ?? null;

        setSelectedWallet(wallet);

        if (wallet) {
          setBuyAccount(wallet.id, "crypto");
        }
      }}
      subtitle=""
      title={`Select wallet for ${selectedOptions.down.code}`}
      placeHolder="Choose"
      options={
        approvedCryptoAccounts
          ?.filter(
            (option) => option.currency === selectedOptions.down.code
          )
          .map((option) => ({
            label: option.name,
            value: option.id,
          })) ?? []
      }
    />
  )}

  {/* NETWORK SELECTOR */}
  {!sendsFiatToCryptoWithoutBalance && currentCurrency !== "BTC" ? (
    <DropDownComponent
      buttonText="Select Network"
      actionCallback={(id: string | number) => {
        setSelectedNetwork(
          filteredNetworks.find(
            (network) => network.value === id
          )?.value ?? "-"
        );
      }}
      subtitle=""
      title="Select Network"
      placeHolder="Choose"
      options={filteredNetworks}
    />
  ) : currentCurrency === "BTC" ? (
    <div />
  ) : null}

  {/* WARNING BOX */}
  {(sendsFiatToCryptoWithoutBalance ||
    sendsCryptoToCryptoWithoutBalance) && (
    <div
      className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 w-[90%] h-fit bg-[rgba(242,242,242,0.64)] flex items-start justify-around text-[#5F5F5F] Text regular rounded-[1.5rem] p-4 ${
        isBitramp() ? "!bg-[#262626] !text-[#D2D2D2]" : ""
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="27"
        viewBox="0 0 30 27"
        fill="none"
      >
        <g clipPath="url(#clip0_4545_15707)">
          <path
            d="M14.414 0.0388907C16.0041 -0.182479 17.5186 0.560181 18.4849 1.78307L29.5059 20.712C30.9717 23.5077 28.9921 26.764 25.8569 26.9996H4.14296C1.00775 26.764 -0.973625 23.5095 0.493923 20.712C3.914 15.1135 6.94645 9.26502 10.3665 3.6665C11.3996 1.97588 12.1982 0.347738 14.414 0.0388907Z"
            fill={isBitramp() ? "#D2D2D2" : "#5F5F5F"}
          />
        </g>
      </svg>
      <div className="w-[80%]">
        Ensure the network matches the withdrawal address and the
        deposit platform supports it, or assets may be lost
      </div>
    </div>
  )}
</div>

        </div>
      </div>
      {notEnoughBalanceModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <div
            className={`min-w-[30rem]  w-[20%] max-w-[70rem] h-auto pb-[2rem] bg-white rounded-[1.5rem] border text-center relative px-8 py-8 pt-6 flex flex-col justify-center items-center`}
          >
            <div className="text-[#05004E] pt-[1rem] text-center text-[1.5rem] font-[700]">
              Not enough balance
            </div>
            <div className=" error text-red-500 mt-8">
              {err?.message ? err.message : "Something went wrong"}
            </div>
            <div
              onClick={() => setNotEnoughBalanceModal(false)}
              className="button--classic w-fit mt-8"
            >
              Ok
            </div>
          </div>
        </div>
      )}
      {(exchangeModal && sendsCryptoToCryptoWithoutBalance) ||
      (exchangeModal && sendsFiatToCryptoWithoutBalance) ||
      (exchangeModal && sendsCryptoToFiatWithoutBalance) ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <div
            ref={modalRef}
            className={`min-w-[30rem]  w-[70%] max-w-[70rem] h-auto pb-[2rem] Container  text-center relative px-8 py-8 pt-6 ${
              exchangeStep === 1 || exchangeStep === 3 ? "!max-w-[30rem]" : ""
            }
            ${isBitramp() ? "" : "border"}}`}
          >
            {exchangeStep === 1 ? (
              <div className="flex flex-col justify-center items-center">
                {" "}
                <div
                  className={`text-[#05004E] pt-[2.5rem] text-center text-[1.5rem] font-[700] ${
                    isBitramp() ? "!text-[#fff]" : ""
                  }`}
                >
                  Confirm Exchange
                </div>
                <div
                  className={`text-[#737791] text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-center items-center gap-2 ${
                    isBitramp() ? "!text-[#9F9F9F]" : ""
                  }`}
                >
                  The price will be recalculated in{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_2348_4001)">
                      <path
                        d="M9.5 0C4.25326 0 0 4.25355 0 9.50018C0 14.7468 4.25326 19 9.5 19C14.7467 19 19 14.7468 19 9.50018C19 4.25355 14.7467 0 9.5 0ZM13.9057 12.3941C13.6774 12.7417 13.2977 12.9302 12.9108 12.9302C12.6868 12.9302 12.4604 12.8671 12.2594 12.7347L8.31104 10.1417V4.0143C8.31104 3.35747 8.84352 2.82536 9.5 2.82536C10.1565 2.82536 10.689 3.35747 10.689 4.0143V8.85867L13.5647 10.7475C14.1136 11.1082 14.266 11.8452 13.9057 12.3941Z"
                        fill={`${isBitramp() ? "#9F9F9F" : "#737791"}`}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2348_4001">
                        <rect width="19" height="19" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>{" "}
                  00:
                  {timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                </div>
                <ButtonComponent
                  title={
                    createTransactionMutation.isPending
                      ? "Loading..."
                      : "Accept & Pay"
                  }
                  actionCallback={() => {
                    if (!exchangeData) return;
                    handleCreateTransaction({
                      rate_id: exchangeData?.id,
                      amount: selectedOptions.up.price,
                      buy_bank_account_id: selectedAccounts.buy_bank_account,
                      buy_crypto_account_id:
                        selectedAccounts.buy_crypto_account,
                      sell_bank_account_id:
                        selectedAccounts.sell_bank_account,
                      sell_crypto_account_id:
                        selectedAccounts.sell_crypto_account,
                      client_id: selectedClient?.id ?? null,
                    });
                    {
                      setSelectedAccounts({
                        buy_bank_account: null,
                        buy_crypto_account: null,
                        sell_bank_account: null,
                        sell_crypto_account: null,
                      });
                    }
                    {
                      setResetSignal((prev) => prev + 1);
                    }

                    if (
                      sendsCryptoToCryptoWithoutBalance ||
                      sendsCryptoToFiatWithoutBalance
                    ) {
                      setQrModal(true);
                      setExchangeModal(false);
                      return;
                    }

                    {
                      selectedOptions?.up?.type === "fiat"
                        ? setExchangeStep(2)
                        : setExchangeStep(3);
                    }
                    setForgotBankAccount(false);
                    setForgotWallet(false);
                    setForgotNetwork(false);
                  }}
                  name="Primary_button mt-4"
                />
                <div
                  className={`Outline_red_button mt-2 ${
                    isBitramp() ? "!text-[#f00] !bg-transparent" : ""
                  }`}
                  onClick={() => {
                    setManualClose(true), setExchangeModal(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <path
                      d="M15.8026 14.0668C16.4956 15.2312 15.2312 16.4956 14.0668 15.8026L9.53101 11.2822L9.4217 11.3148L4.93315 15.8026C3.76877 16.4956 2.50437 15.2312 3.19743 14.0668L7.71776 9.53101L7.6852 9.4217L3.34782 5.09207C2.65244 4.37654 3.11448 3.17882 4.06335 3.01835C4.4153 2.95866 4.78043 3.08889 5.05099 3.31138L9.46899 7.71776L9.5783 7.68598L14.0668 3.19743C15.2312 2.50437 16.4956 3.76877 15.8026 4.93315L11.2822 9.46899L11.314 9.5783L15.8026 14.0668Z"
                      fill="#FF0000"
                    />
                  </svg>
                  Close transaction
                </div>{" "}
              </div>
            ) : null}
            {exchangeStep === 2 ? (
              <div className="flex flex-col">
                <div className="w-full flex justify-end items-center pb-2">
                  <svg
                    onClick={() => {
                      setManualClose(true), setExchangeModal(false);
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 cursor-pointer"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#000"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <div className="flex gap-4">
                  <div
                    onClick={() => setPaymentMethod("card")}
                    className={`flex flex-col justify-start items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative ${
                      isBitramp() && paymentMethod == "card"
                        ? "border-[#8AFEFD]"
                        : ""
                    }`}
                  >
                    {paymentMethod == "bank" && (
                      <div
                        className={`absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem] ${
                          isBitramp()
                            ? "!bg-[rgba(43,43,43,0.7)] !border-[#3E3E3E]"
                            : ""
                        }`}
                      ></div>
                    )}
                    <div className="flex justify-between items-center w-full">
                      <div
                        className={`text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal ${
                          isBitramp() ? "!text-[#fff]" : ""
                        }`}
                      >
                        Pay by Card
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "card"
                              ? isBitramp()
                                ? "bg-[#8AFEFD]"
                                : "bg-[#202A43]"
                              : ""
                          } `}
                        ></div>
                      </div>
                    </div>

                    <form
                      className="h-full flex flex-col w-full items-center justify-center space-y-4"
                      autoComplete="on"
                    >
                      {isLoadingZenLink ? (
                        <div>Generating QR code...</div>
                      ) : null}
                      {zenLink?.error || zenLinkError ? (
                        <div className="text-red-500">
                          Error:{" "}
                          {zenLink?.error?.message ?? "Something went wrong"}
                        </div>
                      ) : null}
                      {zenLink?.data ? (
                        <QRCodeCanvas value={zenLink?.data || ""} size={250} />
                      ) : null}

                      <svg
                        onClick={() =>
                          zenLink?.data && OpenZenCard(zenLink.data)
                        }
                        className="cursor-pointer"
                        width="140"
                        height="81"
                        viewBox="0 0 189 61"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="0.75"
                          y="0.75"
                          width="187.5"
                          height="59.5"
                          rx="11.25"
                          fill="white"
                        />
                        <rect
                          x="0.75"
                          y="0.75"
                          width="187.5"
                          height="59.5"
                          rx="11.25"
                          stroke="#222B45"
                          stroke-width="1.5"
                        />
                        <path
                          d="M72.7746 33.212H69.7156V38H67.2836V24.111H72.7746C75.5676 24.111 77.3726 25.935 77.3726 28.652C77.3726 31.331 75.5486 33.212 72.7746 33.212ZM72.2996 26.277H69.7156V31.046H72.2616C73.9336 31.046 74.8266 30.153 74.8266 28.633C74.8266 27.113 73.9146 26.277 72.2996 26.277ZM81.4983 38.247C79.5033 38.247 78.2873 37.088 78.2873 35.321C78.2873 33.592 79.5413 32.509 81.7643 32.338L84.5763 32.129V31.92C84.5763 30.647 83.8163 30.134 82.6383 30.134C81.2703 30.134 80.5103 30.704 80.5103 31.692H78.5343C78.5343 29.659 80.2063 28.329 82.7523 28.329C85.2793 28.329 86.8373 29.697 86.8373 32.3V38H84.8043L84.6333 36.613C84.2343 37.582 82.9613 38.247 81.4983 38.247ZM82.2583 36.499C83.6833 36.499 84.5953 35.644 84.5953 34.2V33.706L82.6383 33.858C81.1943 33.991 80.6433 34.466 80.6433 35.226C80.6433 36.081 81.2133 36.499 82.2583 36.499ZM88.0377 42.351V40.413H89.4247C90.3367 40.413 90.9067 40.204 91.3057 39.102L91.5717 38.399L87.7907 28.614H90.2417L92.6547 35.454L95.2007 28.614H97.5947L92.9967 40.09C92.2937 41.838 91.3057 42.56 89.7667 42.56C89.1207 42.56 88.5507 42.484 88.0377 42.351ZM106.179 38H104.032V23.864H106.35V29.982C106.958 28.937 108.212 28.31 109.637 28.31C112.316 28.31 113.95 30.4 113.95 33.364C113.95 36.252 112.183 38.247 109.485 38.247C108.079 38.247 106.882 37.62 106.331 36.537L106.179 38ZM106.369 33.269C106.369 34.96 107.414 36.119 109.01 36.119C110.644 36.119 111.613 34.941 111.613 33.269C111.613 31.597 110.644 30.4 109.01 30.4C107.414 30.4 106.369 31.578 106.369 33.269ZM114.627 42.351V40.413H116.014C116.926 40.413 117.496 40.204 117.895 39.102L118.161 38.399L114.38 28.614H116.831L119.244 35.454L121.79 28.614H124.184L119.586 40.09C118.883 41.838 117.895 42.56 116.356 42.56C115.71 42.56 115.14 42.484 114.627 42.351ZM140.007 38H130.013V35.739L136.872 26.353H130.165V24.111H139.893V26.277L132.977 35.758H140.007V38ZM150.759 38H142.133V24.111H150.759V26.353H144.565V29.925H150.113V32.072H144.565V35.758H150.759V38ZM155.79 38H153.396V24.111H155.809L162.326 33.953V24.111H164.72V38H162.326L155.79 28.158V38Z"
                          fill="#222B45"
                        />
                        <path
                          d="M39.4999 17H32.4999C25.9002 17 22.6004 17 20.5502 19.0502C19.074 20.5264 18.6606 22.6504 18.5449 26.1875H53.4549C53.3393 22.6504 52.9259 20.5264 51.4496 19.0502C49.3995 17 46.0995 17 39.4999 17Z"
                          fill="#222B45"
                        />
                        <path
                          d="M32.5 45H39.5C46.0996 45 49.3996 45 51.4497 42.9497C53.5 40.8996 53.5 37.5996 53.5 31C53.5 30.2267 53.5 29.4985 53.4967 28.8125H18.5033C18.5 29.4985 18.5 30.2267 18.5 31C18.5 37.5996 18.5 40.8996 20.5502 42.9497C22.6005 45 25.9003 45 32.5 45Z"
                          fill="#222B45"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M24.1875 38C24.1875 37.2752 24.7751 36.6875 25.5 36.6875H32.5C33.2248 36.6875 33.8125 37.2752 33.8125 38C33.8125 38.7248 33.2248 39.3125 32.5 39.3125H25.5C24.7751 39.3125 24.1875 38.7248 24.1875 38Z"
                          fill="white"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M35.5625 38C35.5625 37.2752 36.1502 36.6875 36.875 36.6875H39.5C40.2249 36.6875 40.8125 37.2752 40.8125 38C40.8125 38.7248 40.2249 39.3125 39.5 39.3125H36.875C36.1502 39.3125 35.5625 38.7248 35.5625 38Z"
                          fill="white"
                        />
                      </svg>
                    </form>
                  </div>
                  <div
                    onClick={() => setPaymentMethod("bank")}
                    className={`flex flex-col justify-center items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative ${
                      isBitramp() && paymentMethod == "bank"
                        ? "border-[#8AFEFD]"
                        : ""
                    }`}
                  >
                    {paymentMethod == "card" && (
                      <div
                        className={`absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem] ${
                          isBitramp()
                            ? "!bg-[rgba(43,43,43,0.7)] !border-[#3E3E3E]"
                            : ""
                        }`}
                      ></div>
                    )}
                    <div className="flex justify-between items-center w-full  pb-[2rem]">
                      <div
                        className={`text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal ${
                          isBitramp() ? "!text-[#fff]" : ""
                        }`}
                      >
                        Bank Transfer
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "bank"
                              ? isBitramp()
                                ? "bg-[#8AFEFD]"
                                : "bg-[#202A43]"
                              : ""
                          } `}
                        ></div>
                      </div>
                    </div>
                    <div className="flex">
                      <QRCodeCanvas
                        value={ibanAcc?.data?.iban || ""}
                        size={180}
                      />
                      <div className="flex flex-col-reverse justify-around pl-6">
                        <div>
                          <div
                            className={` font-satoshi text-[1.2rem] w-full font-[500] text-left flex justify-start items-center gap-2 ${
                              isBitramp() ? "!text-[#fff]" : "!text-[#CFCFCF]"
                            }`}
                          >
                            IBAN
                          </div>
                          <div className="flex justify-start items-center gap-2 w-full ">
                            <div
                              className={`text-[#202A43] text-[1.1875rem] font-[700] font-satoshi whitespace-nowrap ${
                                isBitramp() ? "!text-[#fff]" : ""
                              }`}
                              ref={ibanRef}
                            >
                              {copiedIban ? "Copied!" : ibanAcc?.data?.iban}
                            </div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="21.13"
                              viewBox="0 0 23 27"
                              fill="none"
                              onClick={copyIbanToClipboard}
                              className="cursor-pointer"
                            >
                              <path
                                d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                                fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                              />
                              <path
                                d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                                fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                              />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <div
                            className={` font-satoshi text-[1.2rem] w-full font-[500] text-center flex justify-start items-center gap-2 ${
                              isBitramp() ? "!text-[#fff]" : "text-[#CFCFCF]"
                            }`}
                          >
                            SWIFT
                          </div>
                          <div className="flex w-full justify-start items-center gap-2">
                            <div
                              className={`text-[#202A43] font-satoshi text-[1.1875rem] font-[700] whitespace-nowrap ${
                                isBitramp() ? "!text-[#fff]" : ""
                              }`}
                              ref={swiftRef}
                            >
                              {copiedSwift ? "Copied!" : ibanAcc?.data?.swift}
                            </div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="21.13"
                              viewBox="0 0 23 27"
                              fill="none"
                              onClick={copySwiftToClipboard}
                              className="cursor-pointer"
                            >
                              <path
                                d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                                fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                              />
                              <path
                                d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                                fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2 ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      Bank Name and Address
                    </div>
                    <div className="flex w-full justify-start items-center gap-2">
                      <div
                        className={`text-[#202A43] font-satoshi text-[1.1875rem] font-[700] flex flex-col items-start w-full ${
                          isBitramp() ? "!text-[#fff]" : ""
                        }`}
                      >
                        <div>
                          {copiedNameAddress ? "" : ibanAcc?.data?.name}
                        </div>

                        <div ref={nameAddressRef} className="text-left">
                          {copiedNameAddress
                            ? "Copied!"
                            : ibanAcc?.data?.address}
                        </div>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="21.13"
                        viewBox="0 0 23 27"
                        fill="none"
                        onClick={copyNameAddressToClipboard}
                        className="cursor-pointer"
                      >
                        <path
                          d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                          fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                        />
                        <path
                          d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                          fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                        />
                      </svg>
                    </div>
                    <div
                      className={`text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2 ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      Transfer description
                    </div>
                    <div className="flex w-full justify-start items-center gap-2">
                      <div
                        className={`text-[#202A43] font-satoshi text-[1.1875rem] font-[700] flex flex-col items-start w-full ${
                          isBitramp() ? "!text-[#fff]" : ""
                        }`}
                      >
                        <div ref={transactionIdRef}>
                          {copiedTransactionID
                            ? "Copied!"
                            : justCreatedTransaction}
                        </div>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="21.13"
                        viewBox="0 0 23 27"
                        fill="none"
                        onClick={copyTransactionIDToClipboard}
                        className="cursor-pointer"
                      >
                        <path
                          d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                          fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                        />
                        <path
                          d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                          fill={`${isBitramp() ? "#fff" : "#222B45"}`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="w-full flex justify-center items-center pt-4">
                  <ButtonComponent
                    title="Next step"
                    actionCallback={
                      paymentMethod == "card"
                        ? () => {
                            const data: CardPayment = {
                              payment_channel: "",
                              transaction_id: "",
                              card: {
                                number: "",
                                expiry_date: "",
                                cvv: "",
                              },
                            };
                            handlePostCardPayment(data);
                          }
                        : paymentMethod !== "bank"
                        ? () =>
                            alert(
                              "Please, fill all your data before submitting."
                            )
                        : () => setExchangeStep(3)
                    }
                    name="Primary_button w-fit"
                    plusComponent={true}
                  />
                </div>
              </div>
            ) : null}
            {exchangeStep === 3 ? (
              <div>
                <div className="w-full items-center flex justify-center pt-[3rem]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="86"
                    height="129"
                    viewBox="0 0 86 129"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_3308_8423)">
                      <path
                        d="M55.8233 59.4141C53.8082 61.5311 53.0487 64.5741 54.2431 67.3269C55.658 70.5858 63.2529 75.178 66.2724 77.5605C78.7122 87.362 84.1572 98.3733 85.6211 114.242C85.9028 117.285 86.4602 120.464 85.2536 123.396C83.9735 126.494 80.9845 128.698 77.6648 129.006H8.1592C2.3589 128.025 -0.20744 123.717 0.0130569 118.007C0.625549 102.317 5.89298 89.0162 17.9836 78.9431C21.487 76.0236 29.431 71.4253 31.4645 67.8022C33.6817 63.8519 31.5564 60.5744 28.5797 57.9759C25.9031 55.6367 22.5711 53.6677 19.7475 51.4457C7.31396 41.6874 1.85666 30.5958 0.398927 14.7578C0.123305 11.7643 -0.421813 8.64115 0.705173 5.74636C1.94241 2.58 4.99874 0.308612 8.3552 0H77.8608C81.1438 0.419713 84.096 2.62938 85.3148 5.74636C86.4418 8.64115 85.8967 11.7643 85.6211 14.7578C84.1633 30.6082 78.6999 41.6688 66.2724 51.4395C63.0569 53.9701 58.5673 56.5255 55.8233 59.4079V59.4141ZM42.1709 89.0717C41.0929 89.2569 39.6658 90.337 38.7899 91.0036C29.9272 97.7746 21.8361 105.675 12.9734 112.465C11.0073 114.014 11.5095 117.094 14.0514 117.52H71.9747C74.7371 116.81 74.9392 114.217 72.9486 112.366C71.4909 111.008 69.5922 109.712 68.0425 108.409C60.76 102.299 53.4652 96.1945 46.146 90.1148C44.8413 89.3742 43.7327 88.7878 42.1709 89.0594V89.0717Z"
                        fill="#D2D2D2"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3308_8423">
                        <rect width="86" height="129" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div
                  className={`w-full text-[#202A43] font-satoshi text-[1.1875rem] pt-[1rem] font-[500] text-center ${
                    isBitramp() ? "text-[#D2D2D2]" : ""
                  }`}
                >
                  Transaction is in pending
                </div>
                <div className="w-full flex justify-center items-center">
                  <ButtonComponent
                    title="Done"
                    actionCallback={() => {
                      setExchangeModal(false);
                      setQrModal(false); // Open the QR code modal after transaction is done
                    }}
                    name="Primary_button mt-4"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : qrModal &&
        (sendsCryptoToCryptoWithoutBalance ||
          sendsCryptoToFiatWithoutBalance) ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <Fragment>
            <div
              ref={modalRef}
              className={`min-w-[30rem] flex flex-col items-center w-[60%] max-w-[70rem] h-auto Container border text-center relative pt-20 pb-10`}
            >
              <div className="flex items-center justify-center gap-8 w-full mb-20">
                <div
                  className={`${
                    !walletAddress ? "blur-md" : ""
                  } transition duration-300 p-5`}
                >
                  <QRCodeCanvas
                    value={walletAddress || "https://leonext.exchange/"}
                    size={300}
                    bgColor={"#202a43"}
                    fgColor={"#ffffff"}
                  />
                </div>
                <div className="flex flex-col items-start gap-8 Subtitle">
                  <div className="text-left flex flex-col gap-2">
                    <div
                      className={`dark-gray medium ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      Currency
                    </div>
                    <div
                      className={`dark-blue ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      {walletCurrency}
                    </div>
                  </div>
                  <div className="text-left flex flex-col gap-2">
                    <div
                      className={`dark-gray medium ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      Network
                    </div>
                    <div
                      className={`dark-blue ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      {walletNetwork}
                    </div>
                  </div>
                  <div className="text-left flex flex-col gap-2">
                    <div
                      className={`dark-gray medium ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      Address
                    </div>
                    <div
                      className={`dark-blue ${
                        isBitramp() ? "!text-[#fff]" : ""
                      }`}
                    >
                      {walletAddress}
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <p
                  className={`mt-4 text-center text-black ${
                    isBitramp() ? "!text-[#fff]" : ""
                  }`}
                >
                  Loading your wallet address...
                </p>
              ) : null}

              {wallet?.error || error ? (
                <p className="mt-4 text-red-500 text-center">
                  {wallet?.error?.message
                    ? wallet?.error?.message
                    : "Something went wrong"}
                </p>
              ) : null}

              <ButtonComponent
                title="Next"
                actionCallback={() => {
                  setExchangeModal(true);
                  selectedOptions?.up?.type === "fiat"
                    ? setExchangeStep(2)
                    : setExchangeStep(3);
                }}
                name="Primary_button"
              />
            </div>
          </Fragment>
        </div>
      ) : null}
    </div>
  );
};

export default Exchange;
